import React from 'react';
import { Edit2, Trash2, Clock, Tag, Zap, Copy } from 'lucide-react';

const SnippetCard = ({ snippet, onEdit, onDelete }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLanguageColor = (language) => {
    const colors = {
      javascript: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      python: 'bg-blue-100 text-blue-800 border-blue-200',
      java: 'bg-red-100 text-red-800 border-red-200',
      cpp: 'bg-purple-100 text-purple-800 border-purple-200',
      html: 'bg-orange-100 text-orange-800 border-orange-200',
      css: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      json: 'bg-gray-100 text-gray-800 border-gray-200',
      markdown: 'bg-green-100 text-green-800 border-green-200',
      sql: 'bg-pink-100 text-pink-800 border-pink-200',
      text: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[language] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(snippet.content);
      // You could add a toast notification here
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(snippet);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(snippet.id);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20">
              <Tag className="w-3 h-3 mr-1" />
              {snippet.tag}
            </span>
            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getLanguageColor(snippet.language)}`}>
              {snippet.language}
            </span>
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={handleCopy}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Copy content"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={handleEdit}
              className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Edit snippet"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title="Delete snippet"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
          {snippet.title}
        </h3>
      </div>

      {/* Content Preview */}
      <div className="p-4">
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mb-4">
          <code className="text-sm text-gray-700 dark:text-gray-300 font-mono break-all">
            {snippet.content.length > 100 
              ? `${snippet.content.substring(0, 100)}...` 
              : snippet.content
            }
          </code>
        </div>

        {/* Meta Information */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>{formatDate(snippet.updatedAt)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Zap className="w-3 h-3" />
              <span>{snippet.typingSpeed || 80}ms</span>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <span>Used {snippet.usageCount || 0} times</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SnippetCard;
